import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import {
  BookOpen, History, Users, Scroll, Search, ArrowRight, ChevronRight, Download,
  Smartphone, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { VerseOfTheDay } from '../components/VerseOfTheDay';
import { GlobalSearchBar } from '../components/GlobalSearchBar';
import NextPrayerWidget from '../components/prayer/NextPrayerWidget';
import MobileHomePage from './MobileHomePage';
import { Capacitor } from '@capacitor/core';

const FEATURES = [
  { icon: BookOpen, key: 'feature_quran', link: '/quran',
    color: 'from-emerald-500/20 to-emerald-900/20' },
  { icon: History, key: 'feature_prophet', link: '/prophet-timeline',
    color: 'from-amber-500/20 to-amber-900/20' },
  { icon: Users, key: 'feature_companions', link: '/companions',
    color: 'from-blue-500/20 to-blue-900/20' },
  { icon: Scroll, key: 'feature_prophets', link: '/prophets',
    color: 'from-purple-500/20 to-purple-900/20' },
];

const Landing = () => {
  const { t } = useTranslation();
  const [showMobilePrompt, setShowMobilePrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Capacitor.getPlatform() is synchronous — compute once, no state needed
  const platform = Capacitor.getPlatform();
  const isNativeApp = platform === 'ios' || platform === 'android';

  // Détect mobile web (uniquement si pas dans l'app Capacitor)
  useEffect(() => {
    if (isNativeApp) return; // nothing to do in native app

    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || window.innerWidth < 768;
      setIsMobile(isMobileDevice);
      if (isMobileDevice && !localStorage.getItem('mobilePromptDismissed')) {
        setShowMobilePrompt(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isNativeApp]);

  // Si c'est une app native Capacitor, afficher MobileHomePage
  if (isNativeApp) {
    return <MobileHomePage />;
  }

  const dismissMobilePrompt = () => {
    setShowMobilePrompt(false);
    localStorage.setItem('mobilePromptDismissed', 'true');
  };

  const handleDownloadApk = async () => {
    try {
      const platform = Capacitor.getPlatform();
      
      // Sur Capacitor (dans l'app), ouvrir le Play Store
      if (platform === 'android' || platform === 'ios') {
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: 'https://play.google.com/store/apps/details?id=com.noorlbayan.app' });
      } else {
        // Sur web, télécharger l'APK directement depuis le dossier public
        const link = document.createElement('a');
        link.href = '/mobile-app/app-release.apk';
        link.download = 'noor-al-bayan.apk';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('[Landing] Download APK failed:', err);
      // Fallback: ouvrir simplement l'URL
      window.open('https://play.google.com/store/apps/details?id=com.noorlbayan.app', '_blank');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Mobile App Prompt Modal */}
      <AnimatePresence>
        {showMobilePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-background rounded-xl shadow-2xl max-w-sm w-full p-6 border border-primary/20"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#3DDC84] to-[#2a9d6f] flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-serif text-text-primary">Application Mobile</h3>
                </div>
                <button
                  onClick={dismissMobilePrompt}
                  className="text-text-muted hover:text-text-primary transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-text-secondary mb-6">
                Une application mobile optimisée pour une meilleure expérience utilisateur. Accédez à Noor Al-Bayan partout, à tout moment!
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    handleDownloadApk();
                    dismissMobilePrompt();
                  }}
                  className="w-full bg-gradient-to-r from-[#3DDC84] to-[#2a9d6f] hover:from-[#2a9d6f] hover:to-[#1a6d45] text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Télécharger l'app Android
                </button>
                <button
                  onClick={dismissMobilePrompt}
                  className="w-full bg-text-muted/10 hover:bg-text-muted/20 text-text-primary font-semibold py-3 px-4 rounded-lg transition-all duration-300"
                >
                  Continuer sur le web
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1661630803143-de2704595708?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxncmFuZCUyMG1vc3F1ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzU0NjkwNDh8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/70 to-background" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="overline mb-4 block">{t('landing.welcome_overline')}</span>
            <h1
              className="font-arabic text-5xl sm:text-6xl lg:text-7xl text-primary mb-4"
              dir="rtl"
            >
              {t('landing.app_name_arabic')}
            </h1>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-text-primary mb-6">
              {t('landing.app_name')}
            </h2>
            <p className="text-lg sm:text-xl text-text-secondary max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('landing.hero_long_subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quran">
                <Button
                  size="lg"
                  className="bg-primary text-background hover:bg-primary-hover px-8"
                  data-testid="start-studying-btn"
                >
                  {t('landing.cta_start_studying')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/search">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary/50 text-primary hover:bg-primary/10 px-8"
                  data-testid="explore-btn"
                >
                  <Search className="mr-2 w-5 h-5" />
                  {t('landing.cta_explore')}
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex justify-center" data-testid="hero-search-wrapper">
              <GlobalSearchBar
                variant="hero"
                placeholder={t('landing.search_placeholder')}
              />
            </div>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Download Mobile App Section — MOVED UP */}
      <section className="py-16 px-4 bg-gradient-to-br from-primary/10 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-4">
              {t('landing.download_app_title') || 'Télécharger l\'application'}
            </h2>
            <p className="text-text-secondary mb-12 max-w-2xl mx-auto">
              {t('landing.download_app_subtitle') || 'Accédez à Noor Al-Bayan partout, à tout moment'}
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              {/* Android */}
              <button
                onClick={handleDownloadApk}
                className="group cursor-pointer bg-none border-none w-full sm:w-auto"
                title={t('landing.download_android')}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="glass-card p-8 hover:border-primary/30 transition-all duration-300">
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#3DDC84] to-[#2a9d6f] flex items-center justify-center shadow-lg">
                        <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.6915026,0.192399844 C17.4744692,-0.0274626053 17.1020025,-0.0644935353 16.8375722,0.1088899449 L3.50612381,11.2781085 C3.19218622,11.4976048 3.03522713,11.8299827 3.15390849,12.1429624 C3.27258985,12.4559421 3.54240087,12.6694503 3.86224882,12.6694503 L8.50299765,12.6694503 L3.50612381,20.1265502 C3.30108265,20.4087138 3.30108265,20.8021862 3.50612381,21.0843498 C3.71116496,21.3665134 4.05510588,21.3665134 4.25944286,21.0843498 L10.4209464,12.6694503 L8.54589941,12.6694503 L17.6915026,23.8374606 C17.9085359,24.0644435 18.2810026,24.1014744 18.5454329,23.9280949 C18.8098632,23.7547154 18.9657786,23.4575266 18.9657786,23.1350125 L18.9657786,0.534492958 C18.9657786,0.211978848 18.8098632,-0.0644935353 18.5454329,-0.237873095 Z"/>
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-xl font-serif text-text-primary mb-2">Google Play</h3>
                    <p className="text-text-secondary text-sm mb-4">
                      {t('landing.download_android') || 'Télécharger sur Android'}
                    </p>
                    <div className="flex items-center justify-center text-primary group-hover:gap-2 transition-all duration-300">
                      <Download className="w-5 h-5" />
                      <span className="text-sm font-semibold ml-2">Télécharger</span>
                    </div>
                  </div>
                </motion.div>
              </button>

              {/* iOS - Coming soon */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group w-full sm:w-auto"
              >
                <div className="glass-card p-8 hover:border-primary/30 transition-all duration-300 opacity-60 cursor-not-allowed">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2.01.77-3.29.82-1.31.05-2.3-1.3-3.16-2.54C4.62 18.88 3 16.5 3 14.16c0-2.38 1.58-4.69 4.14-4.8 1.29-.05 2.5.87 3.36.87.87 0 2.26-1.06 3.81-.88.64.1 2.45.27 3.61 2.06-1.76 1.06-2.88 2.77-2.75 4.75 1.06 1.27 2.13 1.76 2.73 1.73z M12 2c-.51 0-1.16 1.37-1.16 2.73 0 1.35.65 2.73 1.16 2.73.51 0 1.15-1.38 1.15-2.73C13.15 3.37 12.51 2 12 2z"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-xl font-serif text-text-primary mb-2">App Store</h3>
                  <p className="text-text-secondary text-sm mb-4">
                    {t('landing.coming_soon') || 'Bientôt disponible'}
                  </p>
                  <div className="flex items-center justify-center text-text-muted">
                    <span className="text-sm font-semibold">Prochainement</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <VerseOfTheDay />

      {/* Next prayer widget — surfaces today's upcoming prayer */}
      <section className="px-4 -mt-4">
        <div className="max-w-3xl mx-auto">
          <NextPrayerWidget />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="overline mb-2 block">{t('landing.explore_knowledge')}</span>
            <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-4">
              {t('landing.complete_study_title')}
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t('landing.complete_study_subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={feature.link}
                  data-testid={`feature-${feature.key}`}
                >
                  <div
                    className={`glass-card p-8 h-full hover:border-primary/30 transition-all duration-300 group bg-gradient-to-br ${feature.color}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center">
                        <feature.icon className="w-7 h-7 text-primary" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="text-xl font-serif text-text-primary mb-1">
                      {t(`landing.${feature.key}_title`)}
                    </h3>
                    <p className="font-arabic text-primary text-lg mb-3" dir="rtl">
                      {t(`landing.${feature.key}_arabic`)}
                    </p>
                    <p className="text-text-secondary text-sm">
                      {t(`landing.${feature.key}_desc`)}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 bg-surface/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline mb-4 block">{t('landing.mission_overline')}</span>
            <h2 className="text-3xl sm:text-4xl font-serif text-text-primary mb-6">
              {t('landing.mission_title')}
            </h2>
            <p className="text-lg text-text-secondary leading-relaxed mb-8">
              <Trans
                i18nKey="landing.mission_text"
                components={{ q: <span className="text-primary" /> }}
              />
            </p>

            <div className="flex flex-wrap justify-center gap-3">
              <span className="badge-quran px-3 py-1.5 rounded border text-sm">{t('landing.badge_quran')}</span>
              <span className="badge-hadith px-3 py-1.5 rounded border text-sm">{t('landing.badge_hadith')}</span>
              <span className="badge-tafsir px-3 py-1.5 rounded border text-sm">{t('landing.badge_tafsir')}</span>
              <span className="badge-historical px-3 py-1.5 rounded border text-sm">{t('landing.badge_historical')}</span>
              <span className="badge-scholarly px-3 py-1.5 rounded border text-sm">{t('landing.badge_scholarly')}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-8 sm:p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-serif text-text-primary mb-4">
                {t('landing.cta_section_title')}
              </h2>
              <p className="text-text-secondary mb-8 max-w-xl mx-auto">
                {t('landing.cta_section_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/quran">
                  <Button className="bg-primary text-background hover:bg-primary-hover">
                    <BookOpen className="mr-2 w-4 h-4" />
                    {t('landing.cta_open_quran')}
                  </Button>
                </Link>
                <Link to="/prophet-timeline">
                  <Button
                    variant="outline"
                    className="border-primary/50 text-primary hover:bg-primary/10"
                  >
                    <History className="mr-2 w-4 h-4" />
                    {t('landing.cta_view_timeline')}
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Landing;
